// Brand Colors - Centralized color definitions for JavaScript/Vue usage
// These colors should match the CSS variables in _theme.scss

export const theme = {
  // Font families
  fontPrimary: '"Manrope", sans-serif',
  fontSecondary: '"Roboto", sans-serif',

  // Primary brand colors
  primary: "#099483",
  secondary: "#dca134",
  accent: "#65f8c6",
  dark: "#212121",

  // Semantic colors
  positive: "#26a35e",
  negative: "#d92f39",
  info: "#734cc5",
  warning: "#f68720",

  // Grey scale
  black: "#000000",
  white: "#ffffff",
  grey50: "#fafafa",
  grey100: "#f5f5f5",
  grey200: "#eeeeee",
  grey300: "#e0e0e0",
  grey400: "#bdbdbd",
  grey500: "#9e9e9e",
  grey600: "#757575",
  grey700: "#616161",
  grey800: "#424242",
  grey900: "#212121",

  // Grey-blue (GREB) scale
  greb50: "#fafafa",
  greb100: "#f0f3f4",
  greb200: "#dbe2e4",
  greb300: "#b8c6cc",
  greb400: "#8c9fa7",
  greb500: "#6c828b",
  greb600: "#4e6169",
  greb700: "#405056",
  greb800: "#283135",
  greb900: "#171a1b",

  // Emerald (EMER) scale
  emer50: "#f3fafa",
  emer100: "#bbdedb",
  emer200: "#91c9c4",
  emer300: "#62b9ae",
  emer400: "#17a997",
  emer500: "#099483",
  emer600: "#0b8778",
  emer700: "#12776b",
  emer800: "#1b6158",
  emer900: "#1d4e47",

  // Honey scale
  honey50: "#fff8eb",
  honey100: "#ffe9c1",
  honey200: "#ffe0a8",
  honey300: "#ffd17c",
  honey400: "#ffc864",
  honey500: "#f8bc4f",
  honey600: "#dca134",
  honey700: "#12776b",
  honey800: "#ce9121",
  honey900: "#c2830f",

  // Green (GREE) scale
  gree100: "#a4ffe0",
  gree200: "#65f8c6",
  gree300: "#23e7a5",
  gree400: "#16cb8e",

  // Yellow (YELL) scale
  yell100: "#ffeba4",
  yell200: "#ffdd64",
  yell300: "#ffc73d",
  yell400: "#edac25",

  // Danger (DANG) scale
  dang500: "#fffdfe",
  dang600: "#e95e66",
  dang700: "#d92f39",
  dang800: "#a61b23",
  dang900: "#510f17",

  // Success (SUCC) scale
  succ500: "#fdfffd",
  succ600: "#1bc769",
  succ700: "#26a35e",
  succ800: "#1a7442",
  succ900: "#1e2d0c",

  // Warning (WARN) scale
  warn500: "#fefdfd",
  warn600: "#ffaf66",
  warn700: "#f68720",
  warn800: "#d36704",
  warn900: "#531907",

  // Element Plus / UI colors
  elWarning: "#e6a23c",
  elSuccess: "#67c23a",
  elDanger: "#f56c6c",
  elInfo: "#909399",
  elPrimary: "#409eff",

  // Additional colors used in the app
  alertRed: "#fa3e50",
  badgePink: "#aa236e",
  maintenanceBg: "#303742",
  mapGreen: "#1fdb9b",
  flyingGuideInactive: "#31da9c",
  flyingGuideActive: "#1377d5",
  warnOrange: "#ff7f37",
  linkBlue: "#0275d8",

  // Classification colors
  classS: "#808080",
  classM: "#eab144",
  classL: "#429283",
  classXl: "#aa236e",

  // UI Element colors
  elBorder: "#dcdfe6",
  rtkBadge: "#99e4ff",
  inactiveText: "#97979763",
  lightBg: "#f8f9fa",
  lightBorder: "#e9ecef",
  buttonHoverBg: "#e1e5e8",
  linkPrimary: "#3d58db",
  primaryDark: "#0c8978",
  primaryLightBg: "#f8fbfb",

  // Dark theme colors
  darkBgPrimary: "#27292c",
  darkBgSecondary: "#333537",
  darkBgTertiary: "#3b3c3d",
  darkBgQuaternary: "#404143",
  darkBgElevated: "#505255",
  darkBgBlack: "#1a1c1e",

  // Additional UI colors found in codebase
  borderLight: "#e1e1e1",
  borderDark: "#c0c0c038",
  borderSeparator: "#4c4c4c",
  borderDivider: "#7f7777",
  
  textMuted: "#7d7d7d",
  textLight: "#d8d8d8",
  textSubtle: "#666",
  textPlaceholder: "#aaaaaa",
  textDark: "#333",

  errorRed: "#ff5050",
  disabled: "#a0a0a0",
  hoverBg: "#707070",
  
  // 3D Viewer specific colors
  viewerHighlight: "#72f0b0",
  viewerGreenLight: "#1dd196",
  viewerGreenDark: "#19c085",
  viewerGreenDarker: "#0d5e43",

  // 3D Viewer semantic color tokens (read by viewer-colors.js / initViewerColors)
  viewerPointColor: "#1fdb9b",            // POINT_COLOR — main interactive point
  viewerSelectedPointColor: "#0f6d4d",    // SELECTED_POINT_COLOR
  viewerMidpointColor: "#23e7a5",         // MIDPOINT_COLOR
  viewerMeasurementPointColor: "#0a6043", // MEASUREMENT_POINT_COLOR
  viewerRestrictedAreaColor: "#e43f32",   // RESTRICTED_AREA_COLOR
  viewerSelectedLineColor: "#ffdd79",     // SELECTED_LINE_COLOR
  viewerOutlineColor: "#fff5d4",          // OUTLINR_COLOR (panel outline)

  // Mode specific colors
  modeArea: "#c4724c",
  modeMeasure: "#1557b5",
  modeSolar: "#1af4aa",
  modeInactive: "#aba9aa",
  modeRestricted: "#711103",

  // Tailwind-like colors used in CSV visualizer
  blue500: "#3b82f6",
  blue600: "#2563eb",
  green500: "#22c55e",
  green600: "#16a34a",
  red400: "#f87171",
  red500: "#ef4444",

  // Additional form/input colors
  inputBorder: "#dddddd",
  inputBorderLight: "#d9d9d9",
  dashedBorder: "#4488ee",
  paginationBorder: "#ddd",
  dottedBorder: "#d8d8d8",
  textSecondary: "#7e7e7e",
  textMedium: "#636c71",
  textTertiary: "#71797d",
  textDim: "#464a4c",
  borderGrey: "#e3e3e3",
  borderWhiteAlpha: "#ffffff52",
  
  // Additional accent colors
  lila: "#a385e2",
  gold: "#ffd700",
}

// Helper function to get CSS variable value
export function getCssVar(varName) {
  return getComputedStyle(document.documentElement).getPropertyValue(
    `--color-${varName}`
  )
}

export default theme
